// What finance did with this project — on the AM's card.
// TCSD-0102, Phase 6 option A (shipped after C). 2026-08-21.
//
// Ben: "It is important for the AM to look back and see resolution... I am open
// to ideas on how to let them know that it was handed off well to finance/hr."
// C is the push (the AM is now a routed recipient of ready_to_invoice and
// project_payment_minted on their own projects). This is the record.
//
// WHAT IT DELIBERATELY DOES NOT SAY
// It never says "seen". Nothing in this app dispatches notify_triage('seen') —
// the action exists in the RPC and no screen calls it — so seen_at is only ever
// written as a COALESCE side-effect at the same instant as the real action. On
// project notifications seen_at and done_at hold the SAME timestamp for
// essentially every row. A "Seen 8/19 · Worked 8/20" badge would print one event
// as two, on dates that never happened.
//
// done_at is rendered as "cleared", not "worked on": notify_close_entity()
// stamps done_at on every active recipient when anyone closes the entity, and
// 812 rows were stamped by a single maintenance sweep on 2026-08-13. "Cleared"
// is true of both cases. "Worked on it" would not be.
//
// The titles come from the notifications themselves, so no event vocabulary is
// typed in this file.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';

interface HandoffRow {
  event_type: string;
  title: string | null;
  raised_at: string | null;
  person_eid: string | null;
  person_name: string | null;
  is_finance: boolean | null;
  read_at: string | null;
  done_at: string | null;
  status: string | null;
}

/** Bare DATE-safe short form. These are timestamptz, so no TCSD-0070 hazard. */
function shortDate(ts: string | null): string {
  if (!ts) return '';
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function stateFor(r: HandoffRow): { text: string; tone: 'done' | 'open' } {
  const who = r.person_name ?? r.person_eid ?? 'finance';
  if (r.done_at) return { text: `Cleared by ${who} · ${shortDate(r.done_at)}`, tone: 'done' };
  if (r.read_at) return { text: `Opened by ${who} · ${shortDate(r.read_at)}`, tone: 'open' };
  return { text: `Waiting on ${who}`, tone: 'open' };
}

export function FinanceHandoffStrip({ projectId }: { projectId: string }) {
  const [rows, setRows] = useState<HandoffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: e } = await supabase.rpc('project_finance_handoff', {
        p_project_id: projectId,
      });
      if (cancelled) return;
      if (e) setError(errorMessage(e, 'Could not load the finance handoff.'));
      else setRows((data ?? []) as HandoffRow[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Finance first — that is the question the AM is asking. Everyone else's rows
  // are kept, because "handed off to finance/hr" includes hr.
  const ordered = [...rows].sort((a, b) => Number(b.is_finance) - Number(a.is_finance));

  if (loading) {
    return (
      <p className="mt-3 text-[11px] text-tcs-text-muted">Loading what finance did with this…</p>
    );
  }
  if (error) {
    return <p className="mt-3 text-[11px] text-st-danger-fg">{error}</p>;
  }
  if (ordered.length === 0) {
    return (
      <div className="mt-3 rounded-md border border-tcs-border bg-tcs-bg-muted/40 p-3">
        <p className="text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
          Finance handoff
        </p>
        <p className="mt-1 text-xs text-tcs-text-secondary">
          Nothing has been routed to finance for this project yet. It reaches them when the project
          is marked ready to invoice.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-tcs-border bg-tcs-bg-muted/40 p-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
        Finance handoff
      </p>
      <ul className="mt-2 space-y-1.5">
        {ordered.map((r) => {
          const state = stateFor(r);
          return (
            <li key={`${r.event_type}-${r.person_eid}-${r.raised_at}`} className="text-xs">
              <span className="text-tcs-text">{r.title ?? r.event_type}</span>
              <span className="text-tcs-text-muted"> · raised {shortDate(r.raised_at)}</span>
              <span
                className={
                  state.tone === 'done'
                    ? 'ml-1 font-medium text-st-ok-fg'
                    : 'ml-1 font-medium text-tcs-text-secondary'
                }
              >
                — {state.text}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-[11px] text-tcs-text-muted">
        &ldquo;Cleared&rdquo; means the item was closed out of that person&rsquo;s work list.
      </p>
    </div>
  );
}
